import { getCachedAppConfiguration } from "appConfig";
import {
  type MediaItem,
  type MediaItemBase,
  mimeTypeAudioMp3,
  mimeTypeAudioWav,
  mimeTypeJpeg,
  mimeTypeMp4,
  mimeTypePng,
  allVideoMimeTypes,
} from "../services/mediaBuilder/contracts";
import { getMediaById } from "../services/mediaBuilder/mediaService";

export function isExistingItem(item: MediaItem) {
  return item.id && item.id !== "";
}

export function isExistingItemOrHaveContent(item: MediaItem) {
  return isExistingItem(item) || (item.content && item.content !== "");
}

export function getBase64Source(item: MediaItemBase): string {
  if (item.content) {
    return `data:${item.mimeType};base64,${item.content}`;
  }
  console.error('getBase64Source: item.content is empty', item);
  return "";
}

export class MediaContent {
  dataUrl = "";

  constructor(dataUrl: string) {
    this.dataUrl = dataUrl;
  }

  static fromBase64(base64Content: string, mimeType: string): MediaContent {
    return new MediaContent(`data:${mimeType};base64,${base64Content}`);
  }

  static fromMedia(item: MediaItemBase): MediaContent {
    return MediaContent.fromBase64(item.content, item.mimeType);
  }

  async getBlob() {
    const response = await fetch(this.dataUrl);
    return response.blob();
  }
}

export function getMediaFileExtension(item: MediaItem): string {
  switch (item.mimeType) {
    case mimeTypeJpeg:
      return "jpg";
    case mimeTypePng:
      return "png";
    case mimeTypeMp4:
      return "mp4";
    default:
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw `invalid mime-type: ${item.mimeType}`;
  }
}

export function hasContent(item: MediaItem) {
  return item.content && item.content !== "";
}

export function getMediaFileName(item: MediaItem): string {
  return `${item.id}.${getMediaFileExtension(item)}`;
}

export function hasImageContent(item: MediaItem): boolean {
  return item.mimeType == mimeTypeJpeg || item.mimeType == mimeTypePng;
}

export function hasAudioContent(item: MediaItem): boolean {
  return item.mimeType == mimeTypeAudioMp3 || item.mimeType == mimeTypeAudioWav;
}

export function hasVideoContent(item: MediaItem): boolean {
  return allVideoMimeTypes.includes(item.mimeType);
}

export { mimeTypePng };

export async function getPreviewUrl(previewId: string | null | undefined) {
  const { mediaBuilderApiUrl } = await getCachedAppConfiguration();
  return previewId ? `${mediaBuilderApiUrl}/api/media/${previewId}/data` : "";
}

export async function getContentOfMediaSource(
  source: string
): Promise<MediaContent | null> {
  if (source.startsWith("data:")) {
    return new MediaContent(source);
  }
  if (source.startsWith("id:")) {
    const media = await getMediaById(source.substring(3), true);
    return MediaContent.fromMedia(media);
  }

  return null;
  //throw `Unsupported source: ${source}`;
}
